/**
 * Wishlist Integration
 * Connects wishlist system to Shopify theme elements
 */

(function() {
  'use strict';

  // Wait for wishlist modules to load
  function initWishlistIntegration() {
    if (!window.WishlistAdd || !window.WishlistAuth) {
      setTimeout(initWishlistIntegration, 100);
      return;
    }

    const wishlistAdd = window.WishlistAdd;
    const wishlistAuth = window.WishlistAuth;
    
    // Verify instances are properly initialized
    if (!wishlistAdd || !wishlistAdd.api || !wishlistAdd.auth) {
      console.error('WishlistAdd not properly initialized');
      setTimeout(initWishlistIntegration, 100);
      return;
    }
    
    if (!wishlistAuth || typeof wishlistAuth.checkAuth !== 'function') {
      console.error('WishlistAuth not properly initialized');
      setTimeout(initWishlistIntegration, 100);
      return;
    }

    /**
     * Handle product page "Add to Wishlist" button
     */
    function initProductPageButton() {
      const buttons = document.querySelectorAll('[data-add-to-wishlist]');
      
      buttons.forEach(button => {
        button.addEventListener('click', async (e) => {
          e.preventDefault();
          
          const productId = button.getAttribute('data-product-id');
          const variantId = button.getAttribute('data-variant-id');
          
          // Get product data from button attributes
          const productData = {
            productTitle: button.getAttribute('data-product-title'),
            variantTitle: button.getAttribute('data-variant-title'),
            productHandle: button.getAttribute('data-product-handle'),
            sku: button.getAttribute('data-sku'),
            imageUrl: button.getAttribute('data-image-url')
          };

          // Check auth first before showing loading state
          if (!wishlistAuth || !wishlistAuth.checkAuth || !wishlistAuth.checkAuth()) {
            // Show auth modal first
            if (wishlistAuth && wishlistAuth.showAuthPrompt) {
              wishlistAuth.showAuthPrompt();
              
              // Listen for successful auth, then show add modal
              const retryAdd = async () => {
                try {
                  const result = await wishlistAdd.showAddModal(productId, variantId, productData);
                  
                  if (result.success) {
                    // Show success state
                    button.classList.add('btn-state-complete');
                    showNotification('Item added to wishlist!', 'success');
                    
                    // Reset button after 2 seconds
                    setTimeout(() => {
                      button.classList.remove('btn-state-complete');
                    }, 2000);
                  } else if (!result.cancelled) {
                    const errorMsg = result.error || 'Failed to add item.';
                    showNotification(errorMsg, 'error');
                  }
                } catch (error) {
                  console.error('Add to wishlist error after auth:', error);
                  const errorMsg = error.message || 'Failed to add item. Please try again.';
                  showNotification(errorMsg, 'error');
                }
                // Remove listener after use
                document.removeEventListener('wishlist:auth:success', retryAdd);
              };
              
              // Only retry if auth succeeds
              document.addEventListener('wishlist:auth:success', retryAdd, { once: true });
            }
            return;
          }

          // Show loading state
          button.classList.add('btn-state-loading');
          button.disabled = true;

          try {
            // Show modal with options (quantity, placement, notes)
            const result = await wishlistAdd.showAddModal(productId, variantId, productData);
            
            // Remove loading state immediately (modal handles its own state)
            button.classList.remove('btn-state-loading');
            button.disabled = false;
            
            if (result.success) {
              // Show success state
              button.classList.add('btn-state-complete');
              
              // Show notification
              showNotification('Item added to wishlist!', 'success');
              
              // Reset button after 2 seconds
              setTimeout(() => {
                button.classList.remove('btn-state-complete');
              }, 2000);
            } else if (result.cancelled) {
              // User cancelled - no action needed, button already reset
            } else if (result.requiresAuth) {
              // Auth required - modal already shown, button already reset
            } else {
              // Error occurred
              const errorMsg = result.error || 'Failed to add item.';
              showNotification(errorMsg, 'error');
            }
          } catch (error) {
            console.error('Add to wishlist error:', error);
            button.classList.remove('btn-state-loading');
            button.disabled = false;
            const errorMsg = error.message || 'Failed to add item. Please try again.';
            showNotification(errorMsg, 'error');
          }
        });
      });
    }

    /**
     * Handle collection page "Add to Wishlist" buttons (hover)
     */
    function initCollectionPageButtons() {
      // Use event delegation to handle dynamically added buttons
      // This ensures buttons work even after DOM changes
      document.addEventListener('click', async (e) => {
        const button = e.target.closest('[data-add-to-wishlist-card]');
        if (!button) return;
        
        e.preventDefault();
        e.stopPropagation(); // Prevent product link navigation
        
        // Prevent multiple simultaneous clicks
        if (button.dataset.processing === 'true') {
          return;
        }
        button.dataset.processing = 'true';
        
        // Remove processing flag after operation completes
        const clearProcessing = () => {
          delete button.dataset.processing;
        };
        
        try {
          
          const productId = button.getAttribute('data-product-id');
          let variantId = button.getAttribute('data-variant-id');
          
          // If variant ID is missing or empty, we need to fetch the first available variant
          if (!variantId || variantId.trim() === '') {
            console.warn('Variant ID missing, attempting to fetch product variants');
            // Try to get variant from product handle
            const productHandle = button.getAttribute('data-product-handle');
            if (productHandle) {
              try {
                // Fetch product JSON to get first available variant
                const productResponse = await fetch(`/products/${productHandle}.js`);
                if (productResponse.ok) {
                  const product = await productResponse.json();
                  const firstVariant = product.variants && product.variants.length > 0 
                    ? product.variants.find(v => v.available) || product.variants[0]
                    : null;
                  if (firstVariant) {
                    variantId = firstVariant.id.toString();
                    // Update button attributes for future clicks
                    button.setAttribute('data-variant-id', variantId);
                    button.setAttribute('data-variant-title', firstVariant.title || '');
                    button.setAttribute('data-sku', firstVariant.sku || '');
                  }
                }
              } catch (fetchError) {
                console.error('Failed to fetch product variant:', fetchError);
              }
            }
          }
          
          // Validate IDs before proceeding
          if (!productId || !variantId || productId.trim() === '' || variantId.trim() === '') {
            console.error('Missing product or variant ID:', { productId, variantId });
            clearProcessing(); // Clear processing flag before returning
            showNotification('Unable to add item. Missing product information.', 'error');
            return;
          }
          
          const productData = {
            productTitle: button.getAttribute('data-product-title'),
            variantTitle: button.getAttribute('data-variant-title'),
            productHandle: button.getAttribute('data-product-handle'),
            sku: button.getAttribute('data-sku'),
            imageUrl: button.getAttribute('data-image-url')
          };

          // Check auth first before attempting to add
          if (!wishlistAuth || !wishlistAuth.checkAuth || !wishlistAuth.checkAuth()) {
            clearProcessing(); // Clear processing flag before returning
            // Show auth modal first
            if (wishlistAuth && wishlistAuth.showAuthPrompt) {
              wishlistAuth.showAuthPrompt();
              
              // Listen for successful auth, then show add modal
              const retryAdd = async () => {
                try {
                  // Show modal with options (quantity, placement, notes) - same as product page
                  const result = await wishlistAdd.showAddModal(productId, variantId, productData);

                  if (result.success) {
                    // Visual feedback
                    button.style.transform = 'scale(1.2)';
                    button.style.color = '#2e7d32';
                    
                    setTimeout(() => {
                      button.style.transform = '';
                      button.style.color = '';
                    }, 300);

                    showNotification('Added to wishlist!', 'success');
                  } else if (!result.cancelled) {
                    const errorMsg = result.error || 'Failed to add item.';
                    showNotification(errorMsg, 'error');
                  }
                } catch (error) {
                  console.error('Add to wishlist error after auth:', error);
                  const errorMsg = error.message || 'Failed to add item. Please try again.';
                  showNotification(errorMsg, 'error');
                } finally {
                  clearProcessing(); // Always clear processing flag
                }
                // Remove listener after use
                document.removeEventListener('wishlist:auth:success', retryAdd);
              };
              
              // Only retry if auth succeeds
              document.addEventListener('wishlist:auth:success', retryAdd, { once: true });
            }
            return;
          }

          // Disable button during operation to prevent double-clicks
          const originalDisabled = button.disabled;
          const originalPointerEvents = button.style.pointerEvents;
          button.disabled = true;
          button.style.pointerEvents = 'none';
          
          // Helper to restore button state
          const restoreButton = () => {
            button.disabled = originalDisabled;
            button.style.pointerEvents = originalPointerEvents;
          };

          // User is authenticated, show modal with options (quantity, placement, notes)
          try {
            const result = await wishlistAdd.showAddModal(productId, variantId, productData);

            // Always restore button state
            restoreButton();

            if (result.success) {
              // Visual feedback
              button.style.transform = 'scale(1.2)';
              button.style.color = '#2e7d32';
              
              setTimeout(() => {
                button.style.transform = '';
                button.style.color = '';
              }, 300);

              showNotification('Added to wishlist!', 'success');
            } else if (result.requiresAuth) {
              // This shouldn't happen if we checked above, but handle it anyway
              if (wishlistAuth && wishlistAuth.showAuthPrompt) {
                wishlistAuth.showAuthPrompt();
              }
            } else if (!result.cancelled) {
              const errorMsg = result.error || 'Failed to add item.';
              console.error('Add to wishlist failed:', result);
              showNotification(errorMsg, 'error');
            }
          } catch (error) {
            console.error('Add to wishlist error:', error);
            // Always restore button state on error
            restoreButton();
            const errorMsg = error.message || 'Failed to add item. Please try again.';
            showNotification(errorMsg, 'error');
          } finally {
            // Always clear processing flag
            clearProcessing();
          }
        } catch (error) {
          console.error('Unexpected error in collection button handler:', error);
          clearProcessing();
          showNotification('An error occurred. Please try again.', 'error');
        }
      });
    }

    /**
     * Show notification toast
     */
    function showNotification(message, type = 'success') {
      // Remove existing notification
      const existing = document.querySelector('.wishlist-notification');
      if (existing) {
        existing.remove();
      }

      const notification = document.createElement('div');
      notification.className = `wishlist-notification wishlist-notification--${type}`;
      notification.textContent = message;
      
      document.body.appendChild(notification);
      
      // Trigger animation
      setTimeout(() => {
        notification.classList.add('is-active');
      }, 10);

      // Remove after 3 seconds
      setTimeout(() => {
        notification.classList.remove('is-active');
        setTimeout(() => {
          notification.remove();
        }, 300);
      }, 3000);
    }

    /**
     * Listen for variant changes on product page
     * Update wishlist button data attributes
     */
    document.addEventListener('theme:variant:change', (event) => {
      const variant = event.detail.variant;
      const button = document.querySelector('[data-add-to-wishlist]');
      
      if (button && variant) {
        button.setAttribute('data-variant-id', variant.id);
        button.setAttribute('data-variant-title', variant.title || '');
        button.setAttribute('data-sku', variant.sku || '');
        button.disabled = !variant.available;
      }
    });

    /**
     * Handle header wishlist link clicks
     * Check auth before redirecting to wishlist page
     */
    function initHeaderLinks() {
      // Use event delegation since links might be added dynamically
      document.addEventListener('click', (e) => {
        // Wishlist links (heart icon) - check both the link and any child elements
        const wishlistLink = e.target.closest('[data-wishlist-link]');
        if (wishlistLink) {
          e.preventDefault();
          e.stopPropagation();
          
          // Always redirect to wishlist page - it will handle auth check
          window.location.href = '/pages/wishlist';
          return;
        }

        // Account links (profile icon) - check both data-account-link and data-wishlist-auth-trigger
        const accountLink = e.target.closest('[data-account-link], [data-wishlist-auth-trigger]');
        if (accountLink) {
          e.preventDefault();
          e.stopPropagation();
          
          // If already authenticated, go to wishlist page
          if (wishlistAuth && wishlistAuth.checkAuth && wishlistAuth.checkAuth()) {
            window.location.href = '/pages/wishlist';
          } else if (wishlistAuth && wishlistAuth.showAuthPrompt) {
            // If not authenticated, show auth modal
            wishlistAuth.showAuthPrompt();
          } else {
            console.error('WishlistAuth not ready');
          }
          return;
        }
      });
    }

    /**
     * Initialize on page load
     */
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        initProductPageButton();
        initCollectionPageButtons();
        initHeaderLinks();
      });
    } else {
      initProductPageButton();
      initCollectionPageButtons();
      initHeaderLinks();
    }
  }

  // Start initialization
  initWishlistIntegration();
})();
